/**
 * Remembers, in this browser, that someone ticked "Don't show this again" on an ACH connection guide. There are two
 * guides (for staff on the ACH Consent tab, for the borrower on the public ACH page) and each remembers on its own. The
 * version suffix lets a materially changed guide show itself again by bumping it.
 */
export type AchGuideVariant = 'staff' | 'borrower';

const dismissedKey = (variant: AchGuideVariant) => `roc.achConnectionGuide.${variant}.dismissed.v1`;

const shownThisSession: { [variant in AchGuideVariant]?: boolean } = {};

export const isGuideDismissed = (variant: AchGuideVariant): boolean => {
  try {
    return window.localStorage.getItem(dismissedKey(variant)) === 'true';
  } catch (e) {
    // Storage blocked (private mode, policy): behave as if the guide was never dismissed.
    return false;
  }
};

export const setGuideDismissed = (variant: AchGuideVariant, dismissed: boolean) => {
  try {
    if (dismissed) {
      window.localStorage.setItem(dismissedKey(variant), 'true');
    } else {
      window.localStorage.removeItem(dismissedKey(variant));
    }
  } catch (e) {
    // Nothing to do: the guide just shows again next time.
  }
};

/**
 * A guide opens by itself at most once per page load, so reopening the same screen doesn't pop it up each time. A new
 * session shows it again until it is dismissed for good.
 */
export const shouldAutoOpenGuide = (variant: AchGuideVariant): boolean =>
  !isGuideDismissed(variant) && !shownThisSession[variant];

export const markGuideShownThisSession = (variant: AchGuideVariant) => {
  shownThisSession[variant] = true;
};
